import React, { forwardRef, Ref, type JSX } from 'react'
import Error from '@/components/typography/Error'
import { Label, TextInput as TextInputFB, TextInputProps } from 'flowbite-react'

interface IProps extends TextInputProps {
  id: string
  type?: string
  error?: React.ReactNode | string
  label?: string
  hint?: React.ReactNode
  placeholder?: string
  className?: string
  extraClassName?: string
  containerClassName?: string
  labelClassName?: string
  autoComplete?: string
  rightIcon?: () => JSX.Element
}

const getHelperText = (
  error?: React.ReactNode | string,
  hint?: React.ReactNode
) => {
  if (error) {
    return <Error>{error}</Error>
  }
  if (hint) {
    return hint
  }
  return <></>
}

const TextInput = forwardRef(
  (
    {
      id,
      error,
      label,
      placeholder,
      containerClassName = '',
      labelClassName = '',
      type = 'text',
      rightIcon,
      hint,

      // NOTE: `autoComplete` and `className` are intentionally NOT destructured
      // here. They must fall through `...props` onto the native <input> so that
      // (a) password managers (1Password, Bitwarden, browser built-ins) can
      // recognise username / passcode fields via `autoComplete`, and
      // (b) caller-supplied input styling (e.g. `className="w-full"`) actually
      // reaches the field. Pulling either into an unused local used to silently
      // drop it.
      ...props
    }: IProps,
    ref: Ref<any>
  ) => {
    return (
      <div className={containerClassName}>
        {label && (
          <div className="mb-1">
            <Label
              htmlFor={id}
              className={labelClassName}
              value={label}
              color={error ? 'failure' : undefined}
            />
          </div>
        )}
        <TextInputFB
          id={id}
          type={type}
          placeholder={placeholder}
          color={error ? 'failure' : 'gray'}
          helperText={getHelperText(error, hint)}
          rightIcon={rightIcon}
          ref={ref}
          {...props}
        />
      </div>
    )
  }
)
TextInput.displayName = 'TextInput'

export default TextInput
